import type { AnswerValue, Attempt, Confidence, Exam, MisconceptionCode, Question } from '../types'
import { firebaseAuth } from './firebase'

type HostedSession = { id: string; currentIndex: number; status: 'active' | 'complete' | 'abandoned' }

type HostedQuestion = Omit<Question, 'answer' | 'solution' | 'hint1' | 'hint2' | 'aiContext'>

const toQuestion = (question: HostedQuestion): Question => ({
  ...question,
  // The hosted API deliberately omits these fields until submission. Empty
  // values keep QuestionCard's local-demo contract intact without leaking them.
  answer: '',
  solution: '',
  hint1: '',
  hint2: '',
})

const apiUrl = import.meta.env.VITE_SANGMATH_API_URL?.replace(/\/$/, '')

export const isCloudflarePracticeConfigured = () => Boolean(apiUrl)

async function callWorker<T>(body: Record<string, unknown>): Promise<T> {
  const user = firebaseAuth.currentUser
  if (!apiUrl || !user) throw new Error('Cloudflare practice requires a signed-in Firebase user and VITE_SANGMATH_API_URL.')
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
    body: JSON.stringify(body),
  })
  const data = await response.json().catch(() => ({})) as T & { error?: string }
  if (!response.ok) throw new Error(data.error || 'Cloudflare Worker request failed.')
  return data
}

export async function startHostedPracticeSession(input: { exam: Exam | 'ALL'; topic: string; count?: number }) {
  const data = await callWorker<{ session?: HostedSession; questions?: HostedQuestion[] }>({ action: 'start', exam: input.exam === 'ALL' ? null : input.exam, topic: input.topic === 'ALL' ? null : input.topic, count: input.count ?? 10 })
  if (!data.session || !Array.isArray(data.questions)) throw new Error('Could not start hosted practice.')
  return { session: data.session as HostedSession, questions: data.questions.map(toQuestion) }
}

export async function gradeHostedPracticeAnswer(input: { sessionId: string; instanceId: string; answer: AnswerValue; elapsedSeconds: number; confidence: Confidence; nextIndex: number }) {
  if (typeof input.answer !== 'string') throw new Error('This hosted template does not support the submitted answer type.')
  const data = await callWorker<{ attemptId?: string; correct?: boolean; solution?: string; hint1?: string; hint2?: string }>({ action: 'answer', sessionId: input.sessionId, instanceId: input.instanceId, answer: input.answer, elapsedSeconds: input.elapsedSeconds, confidence: input.confidence, nextIndex: input.nextIndex })
  if (!data.attemptId || typeof data.correct !== 'boolean' || !data.solution || !data.hint1 || !data.hint2) throw new Error('Could not grade hosted practice.')
  return data as { attemptId: string; correct: boolean; solution: string; hint1: string; hint2: string }
}

type HostedAttemptRow = {
  id: string; question_instance_id: string; answer_json: string; correct: number; elapsed_seconds: number
  competency_snapshot_json: string; knowledge_snapshot_json: string; topic_snapshot: string; variant_group_snapshot: string
  prerequisites_snapshot_json: string; confidence: Confidence; misconception?: Attempt['misconception']; hint_level: number
  session_type: Attempt['session']; review_due_at?: string; created_at: string
}

export async function listHostedAttempts(): Promise<Attempt[]> {
  const data = await callWorker<{ attempts?: HostedAttemptRow[] }>({ action: 'attempts' })
  if (!Array.isArray(data.attempts)) return []
  return data.attempts.map(row => ({
    id: row.id,
    questionId: row.question_instance_id,
    answer: JSON.parse(row.answer_json) as AnswerValue,
    correct: Boolean(row.correct),
    timeSeconds: row.elapsed_seconds,
    competency: JSON.parse(row.competency_snapshot_json),
    knowledge: JSON.parse(row.knowledge_snapshot_json),
    topic: row.topic_snapshot,
    variantGroup: row.variant_group_snapshot,
    prerequisites: JSON.parse(row.prerequisites_snapshot_json),
    confidence: row.confidence,
    misconception: row.misconception,
    hintLevel: row.hint_level,
    session: row.session_type,
    at: row.created_at,
    reviewDueAt: row.review_due_at,
    serverPersisted: true,
  }))
}

export async function saveHostedReflection(attemptId: string, misconception: MisconceptionCode) {
  await callWorker<{ ok?: boolean }>({ action: 'reflect', attemptId, misconception })
}
